import { Component, inject, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../environments/environment';
import { User } from './models/user';
import { SpinnerService } from './services/spinner.service';
import { StateService } from './services/state.service';
import { Footer } from './shared/components/footer/footer';
import { SideBar } from './shared/components/side-bar/side-bar';
import { Spinner } from './shared/components/spinner/spinner';
import { Toast } from './shared/components/toast/toast';
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SideBar, Footer, Spinner, Toast],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  title = 'better-front';

  protected stateService = inject(StateService);
  public auth = inject(AuthService);
  private router = inject(Router);
  public spinnerService = inject(SpinnerService);

  constructor() {
    /**
     * This handles showing a loading spinner specifically during the first navigation
     * when the application bootstraps. It automatically cleans itself up after the first
     * navigation completes.
     */
    const navigationEnd$ = new Subject<void>();
    this.router.events.pipe(takeUntil(navigationEnd$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.spinnerService.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        this.spinnerService.hide();
        navigationEnd$.next();
      }
    });
  }

  ngOnInit() {
    let isFirstTime: boolean = true;
    // Get roles on login.
    this.auth.idTokenClaims$.subscribe((claims) => {
      if (claims && claims['sub'] && claims.name && claims.email) {
        const roles: string[] =
          claims[`${environment.auth0.domain}/roles`] || [];
        const user: User = {
          id: claims['sub'],
          name: claims.name,
          email: claims.email,
          roles: roles,
          nickname: claims.nickname ?? claims.email.split('@')[0],
          picture: claims.picture,
        };
        this.stateService.setUserState(user);

        if (isFirstTime) {
          this.stateService.getHospitalData();
          isFirstTime = false;
        }
      }
    });

    // Clear auth state on logout
    this.auth.isAuthenticated$.subscribe((isAuthenticated) => {
      if (!isAuthenticated) {
        this.stateService.clearState();
      }
    });
  }
}
